// src/features/Management/page/UserFormPage.tsx
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import DynamicForm, { type DynamicFormSchema } from '@/common/components/DynamicForm/DynamicForm';
import { useTranslation } from 'react-i18next';

export default function SubMenuForm() {
    const { t } = useTranslation();
    const subMenuFormSchema: DynamicFormSchema = {
        layout: 'vertical', // Overall form layout
        // submitButtonText: 'Save User', // Custom text for the submit button
        showResetButton: true, // Show a reset button

        // Define form sections for better organization and complex layouts
        sections: [
            {
                key: 'sub-menus', // Unique key for the section
                title: t('Sub Menu'), // Title displayed if type is 'card'
                type: 'card', // This section will be rendered inside an Ant Design Card
                layout: { type: 'grid', gutter: 24 }, // Fields within this section use a 2-column grid with 24px gutter
                fields: [

                    {
                        name: 'name',
                        label: 'Name',
                        type: 'input',
                        required: true,
                        requiredMessage: 'Please enter sub menu name.', // Custom required message
                        pattern: 'AlphabetsWithSpace',
                        validation: [
                            { min: 3, message: t('Name must be at least 3 characters long.') },
                            { max: 50, message: t('Name cannot be more than 50 characters long.') },
                        ],
                        autocomplete: 'false',

                        colSpan: 12, // Takes 12 out of 24 grid units (half width)
                    },
                    {
                        name: 'nameAr',
                        label: 'Name Arabic',
                        type: 'input',
                        required: true,
                        requiredMessage: t('Please enter a sub menu name (Arabic).'), // Custom required message
                        autocomplete: 'false',
                        pattern: 'AlphabetsWithSpaceAr',
                        validation: [
                            { min: 3, message: t('Name must be at least 3 characters long.') },
                            { max: 50, message: t('Name cannot be more than 50 characters long.') },
                        ],
                        colSpan: 12, // Takes 12 out of 24 grid units (half width)
                    },
                    {
                        name: 'icon',
                        label: 'Name icon',
                        type: 'input',
                        required: true,
                        requiredMessage: t('Please enter a name icon.'), // Custom required message
                        autocomplete: 'false',
                        pattern: 'Alphabets',
                        validation: [
                            { min: 1, message: t('Icon must be at least 1 characters long.') },
                            { max: 20, message: t('Icon cannot be more than 20 characters long.') },
                        ],
                        colSpan: 12, // Takes 12 out of 24 grid units (half width)
                    },
                    {
                        name: 'displayOrder',
                        label: 'Display Order',
                        type: 'number',
                        required: true,
                        requiredMessage: t('Please Select Display Order.'), // Custom required message
                        autocomplete: 'false',
                        validation: [
                            {
                                validator: (_: unknown, value: number) => {

                                    if (value > 255) {
                                        return Promise.reject(new Error(t('Value cannot be greater than 255.')));
                                    }
                                    if (value < 1) {
                                        return Promise.reject(new Error(t('Value must be at least 1.')));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ],
                        pattern: 'OnlyDigits',
                        colSpan: 12, // Takes 12 out of 24 grid units (half width)
                    },
                    {
                        name: 'menuId',
                        label: 'Menu',
                        type: 'select',
                        showSearch: true,
                        colSpan: 12, // Full width
                        placeholder: t("Select menu"),
                        options: [], // Keep this empty as options will be dynamic
                        dynamicOptions: { // Configuration for fetching options from an API
                            url: "/Lookup/GetDropdownData", // Your API endpoint for skills (ensure it returns an array of objects like {id: 1, name: "JavaScript"})
                            method: "POST", // HTTP method for the request
                            body: { "dropdowns": ["menu"] },
                            // JavaScript function string to transform the API response into { label, value } format
                            transformData: "(apiResponse) => apiResponse.menu.map(item => ({ label: item.Name, value: item.Id }))"
                        },
                    },
                    {
                        name: 'actionId',
                        label: 'Action',
                        type: 'select',
                        showSearch: true,
                        colSpan: 12, // Full width
                        placeholder: t("Select action"),
                        options: [], // Keep this empty as options will be dynamic
                        dynamicOptions: { // Configuration for fetching options from an API
                            url: "/Lookup/GetDropdownData", // Your API endpoint for skills (ensure it returns an array of objects like {id: 1, name: "JavaScript"})
                            method: "POST", // HTTP method for the request
                            body: { "dropdowns": ["action"] },
                            // JavaScript function string to transform the API response into { label, value } format
                            transformData: "(apiResponse) => apiResponse.action.map(item => ({ label: item.Name, value: item.Id }))"
                        },
                    },
                ],
            },
        ],
    };

    return (
        <DynamicForm
            schema={subMenuFormSchema}
            submitApiUrl={API_ENDPOINTS.MANAGEMENT.GET_SUB_MENUS_SAVE}
            getByIdApiUrl={API_ENDPOINTS.MANAGEMENT.GET_SUB_MENUS_ID}
            updateApiUrl={API_ENDPOINTS.MANAGEMENT.GET_SUB_MENUS_UPDATE}
            refreshTableDataKey={QUERY_KEYS.MANAGEMENT.SUBMENUS}
        />
    );
}
